import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { isPushSupported, getPushSubscription, subscribeToPush, sendTestPush } from "@/lib/push";
import { Bell, Check, BellRing, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const popRef = useRef(null);

  const fetchCount = useCallback(() => {
    api.get("/notifications/unread-count")
      .then(r => setUnread(r.data?.count || 0))
      .catch(() => {});
  }, []);

  const fetchList = useCallback(() => {
    api.get("/notifications/mine")
      .then(r => setItems(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, [fetchCount]);

  // Detect existing push subscription
  useEffect(() => {
    if (!isPushSupported()) return;
    getPushSubscription().then(s => setPushOn(!!s)).catch(() => {});
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await subscribeToPush();
      setPushOn(true);
      toast.success("تم تفعيل إشعارات الهاتف ✅");
      // Send a welcome test push
      try { await sendTestPush(); } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(e?.message || "فشل تفعيل الإشعارات");
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  useEffect(() => {
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/read`);
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnread(u => Math.max(0, u - (n.read ? 0 : 1)));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems(prev => prev.map(x => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const onItemClick = async (n) => {
    if (!n.read) await markRead(n);
    setOpen(false);
    if (n.link) nav(n.link);
  };

  return (
    <div className="relative" ref={popRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(o => !o)}
        data-testid="notif-bell-btn"
        className="relative"
      >
        <Bell className="size-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-black flex items-center justify-center shadow"
            data-testid="notif-unread-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute end-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-popover border rounded-xl shadow-2xl z-[60]"
          data-testid="notif-dropdown"
          dir="rtl"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b sticky top-0 bg-popover">
            <div className="font-bold text-sm">الإشعارات</div>
            <div className="flex items-center gap-3">
              {items.some(x => !x.read) && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                  data-testid="notif-mark-all"
                >
                  <Check className="size-3" /> تعليم الكل كمقروء
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={async () => {
                    if (!window.confirm("مسح كل الإشعارات من حسابك؟ (الإدارة ما تزال تراها)")) return;
                    try {
                      await api.delete("/notifications/mine");
                      setItems([]); setUnread(0);
                      toast.success("تم المسح");
                    } catch { toast.error("تعذّر المسح"); }
                  }}
                  className="text-xs text-rose-600 hover:underline flex items-center gap-1"
                  data-testid="notif-clear-all"
                >
                  <Trash2 className="size-3" /> مسح الكل
                </button>
              )}
            </div>
          </div>

          {/* Enable Push banner */}
          {isPushSupported() && !pushOn && (
            <div className="p-3 border-b bg-gradient-to-l from-primary/10 to-accent/10" data-testid="enable-push-banner">
              <div className="flex items-start gap-2">
                <BellRing className="size-4 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 text-xs">
                  <div className="font-bold mb-1">فعّل إشعارات الهاتف</div>
                  <div className="text-muted-foreground mb-2">
                    اسمح بالإشعارات لتصلك الحجوزات والطلبات على شاشة قفل الهاتف فوراً.
                  </div>
                  <Button
                    size="sm"
                    onClick={enablePush}
                    disabled={pushBusy}
                    className="h-7 px-3 text-xs gap-1"
                    data-testid="enable-push-btn"
                  >
                    <BellRing className="size-3" /> {pushBusy ? "..." : "تفعيل الإشعارات"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="notif-empty">
              لا توجد إشعارات بعد
            </div>
          ) : (
            <ul className="divide-y">
              {items.map(n => (
                <li
                  key={n.id}
                  className={`p-3 hover:bg-muted/60 transition ${!n.read ? "bg-primary/5" : ""}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 size-2 rounded-full bg-primary shrink-0" />}
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onItemClick(n)}>
                      <div className="text-sm font-semibold line-clamp-1">{n.title}</div>
                      {n.body && (
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleString("ar") : ""}
                      </div>
                    </div>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await api.delete(`/notifications/${n.id}`);
                          setItems(xs => xs.filter(x => x.id !== n.id));
                        } catch { /* ignore */ }
                      }}
                      className="p-1 rounded-full hover:bg-muted text-muted-foreground"
                      data-testid={`notif-delete-${n.id}`}
                      aria-label="حذف"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
